import axios from 'axios';
import { isTokenExpired } from '@/utils/auth';
import { apiUrl } from '@/utils/config';
import { encryptData, decryptData } from '@/utils/crypto';
let timer = null; // Timer variable to store the token expiration timer

const state = {
    user: getUserFromLocalStorage(),
};
function getUserFromLocalStorage() {
    try {
        return JSON.parse(decryptData(localStorage.getItem('user'))) || null
    }
    catch {
        return null;
    }
}
const mutations = {
    SET_USER(state, user) {
        state.user = user;
    },
};

const actions = {
    async login({ commit, dispatch }, credentials) {
        try {
            await dispatch('showSpinner', null, { root: true });

            const response = await axios.post(`${apiUrl}v1/u/submitlogin`, credentials);
            if (response.data.success) {
                const user = response.data.user;

                commit('SET_USER', user);
                localStorage.setItem('user', encryptData(JSON.stringify(user)));

                // Start token expiration timer
                dispatch('startTokenExpirationTimer', user.expiresIn);
                await dispatch('hideSpinner', null, { root: true });
                return user;
            }
            else {
                throw { error: { code: '', message: response.data.message } }
            }
        } catch (error) {
            throw error.response ? error.response.data : error;
        } finally {
            await dispatch('hideSpinner', null, { root: true });
        }
    },
    async activate({ commit, dispatch }, code) {
        try {
            const response = await axios.get(`${apiUrl}v1/u/activate/${code}`);
            if (response.data.success) {
                const user = response.data.user;

                commit('SET_USER', user);
                localStorage.setItem('user', encryptData(JSON.stringify(user)));

                // Start token expiration timer
                dispatch('startTokenExpirationTimer', user.expiresIn);
                return user;
            }
            else {
                throw { error: { code: '', message: response.data.message } }
            }
        } catch (error) {
            throw error.response ? error.response.data : error;
        } 
    },
    logout({ commit }) {
        commit('SET_USER', null);
        localStorage.removeItem('user');
        // Clear token expiration timer
        clearInterval(timer);
        timer = null;
    },
    async initializeStore({ commit, dispatch }) {
        const user = JSON.parse(decryptData(localStorage.getItem('user')));
        if (user) {
            if (isTokenExpired(user.expiresIn)) {
                // Token has expired, attempt to refresh token
                try {
                    const refreshedUser = await dispatch('refreshToken');
                    commit('SET_USER', refreshedUser);
                    dispatch('startTokenExpirationTimer', refreshedUser.expiresIn);
                } catch (error) {
                    // Handle refresh token failure (e.g., log out user)
                    console.error('Failed to refresh token:', error);
                    commit('SET_USER', null);
                    localStorage.removeItem('user');
                }
            } else {
                // Token is still valid, start token expiration timer
                dispatch('startTokenExpirationTimer', user.expiresIn);
                commit('SET_USER', user);
            }
        }
    },
    startTokenExpirationTimer({ dispatch }, expiresIn) {
        // Clear existing timer if it exists
        if (timer) {
            clearInterval(timer);
            timer = null;
        }

        // Calculate token expiration time
        const expirationTime = expiresIn * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeout = expirationTime - now;

        // Set timer to refresh token 5 minutes before expiration
        const refreshTime = Math.max(5 * 60 * 1000, timeout - (5 * 60 * 1000)); // 5 minutes in milliseconds
        timer = setTimeout(async () => {
            console.log('Token refresh initiated...');
            try {
                const refreshedUser = await dispatch('refreshToken');
                dispatch('startTokenExpirationTimer', refreshedUser.expiresIn);
                console.log('token refreshed successfully!');
            } catch (error) {
                console.error('Failed to refresh token:', error);
                dispatch('logout');
            }
        }, refreshTime);
    },
    async refreshToken({ commit, state }) {
        try {
            // Make a GET request to refresh the token
            const response = await axios.get(
                `${apiUrl}v1/u/refreshtoken`,
                {
                    headers: {
                        Authorization: `Bearer ${state.user.refreshToken}` // Include refresh token as bearer token
                    }
                }
            );

            // If successful, update user state and local storage with refreshed user data
            const refreshedUser = response.data.user;
            commit('SET_USER', refreshedUser);
            localStorage.setItem('user', encryptData(JSON.stringify(refreshedUser)));

            return refreshedUser; // Return refreshed user data if needed
        } catch (error) {
            // Handle error responses, including network errors or invalid token errors
            throw error.response ? error.response.data : error;
        }
    },

};

const getters = {
    isAuthenticated: state => !!state.user && !isTokenExpired(state.user.expiresIn),
    currentUser: state => state.user,
    userRoles: state => state.user ? state.user.roles : []
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
